import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import { generateTask as generateBinomial } from "./MultiplyBinomialsTask";

/* ===================== Helpers: kanoniczny zapis ===================== */

// sortuje litery w części zmiennej: yx -> xy
function sortVariablePart(str) {
  return str.replace(/[a-z]+/gi, (m) => m.split("").sort().join(""));
}

// rozbija "coef*vars" ze stringa typu "-20xz", "xy", "36"
function splitCoeffVar(term) {
  const s = String(term).trim();
  const m = s.match(/^([+-])?(\d+)?([a-z]+)?$/i);
  if (!m) return { c: 0, v: "" };
  const sign = m[1] === "-" ? -1 : 1;
  const digits = m[2] ? Number(m[2]) : (m[3] ? 1 : 0);
  const vars = m[3] ? sortVariablePart(m[3]) : "";
  return { c: sign * digits, v: vars };
}

// łączy współczynnik i część zmienną do stringa (ukrywa 1 przy zmiennej)
function formatCoeffVar(coeff, vars) {
  if (!vars) return String(coeff);
  if (coeff === 1) return vars;
  if (coeff === -1) return `-${vars}`;
  return `${coeff}${vars}`;
}

// porządek: więcej liter -> wcześniej; potem wg liter; na koniec stałe
function compareTermsCanonical(a, b) {
  const va = a.match(/[a-z]+/i)?.[0] || "";
  const vb = b.match(/[a-z]+/i)?.[0] || "";
  if (va.length !== vb.length) return vb.length - va.length;
  const cmp = va.localeCompare(vb);
  if (cmp !== 0) return cmp;
  return a.localeCompare(b);
}

// normalizuje poj. składnik
function normalizeTerm(raw) {
  let s = raw.replace(/\s+/g, "").replace(/\*/g, "");
  let sign = 1;
  if (s.startsWith("-")) {
    sign = -1;
    s = s.slice(1);
  } else if (s.startsWith("+")) {
    s = s.slice(1);
  }

  const vars = s.match(/[a-z]+/i)?.[0] || "";
  const coeffStr = s.replace(/[a-z]+/gi, "");
  let coeff = coeffStr === "" ? 1 : Number(coeffStr);
  if (Number.isNaN(coeff)) coeff = 1;

  const v = sortVariablePart(vars);
  const c = sign * coeff;

  if (!v) return String(c);
  if (c === 1) return v;
  if (c === -1) return `-${v}`;
  return `${c}${v}`;
}

// normalizuje całe wyrażenie do np. "12xy-54x-8y+36"
function normalizeExpression(expr) {
  if (!expr || typeof expr !== "string") return "";
  const prepared = expr
    .replace(/−/g, "-")
    .replace(/\s+/g, "")
    .replace(/\*/g, "")
    .replace(/-/g, "+-")
    .toLowerCase();

  const terms = prepared.split("+").filter(Boolean).map(normalizeTerm);
  terms.sort(compareTermsCanonical);

  return terms
    .map((t, i) => (i === 0 ? t : (t.startsWith("-") ? t : `+${t}`)))
    .join("")
    .replace(/\s+/g, "");
}

// zwraca tablicę znormalizowanych składników, np. ["12xy","-54x","-8y","36"]
function toNormalizedTerms(expr) {
  const s = normalizeExpression(expr);
  return s
    .replace(/-/g, "+-")
    .split("+")
    .filter(Boolean);
}

// buduje wyrażenie kanoniczne z listy składników (sumuje podobne)
function buildCanonicalExpression(terms) {
  const map = new Map(); // key = vars, value = sum coeff
  for (const t of terms) {
    const { c, v } = splitCoeffVar(t);
    map.set(v, (map.get(v) || 0) + c);
  }
  // usuń 0
  const entries = [...map.entries()].filter(([, c]) => c !== 0);
  // posortuj
  entries.sort(([v1, c1], [v2, c2]) => {
    if (v1.length !== v2.length) return v2.length - v1.length;
    if (v1 !== v2) return v1.localeCompare(v2);
    return c1 - c2;
  });
  // sklej
  const parts = entries.map(([v, c]) => formatCoeffVar(c, v));
  return parts
    .map((p, i) => (i === 0 ? p : (p.startsWith("-") ? p : `+ ${p}`)))
    .join(" ")
    .replace(/\+\s-/g, "- ");
}

/* ===================== GENERATE TASK ===================== */
export function generateTask() {
  // ZAWSZE: (..)(..)  ±  (..)(..)
  const left = generateBinomial();
  const right = generateBinomial();

  const sign = Math.random() < 0.5 ? "+" : "-";

  const leftTerms = toNormalizedTerms(left.data.expected);
  let rightTerms = toNormalizedTerms(right.data.expected);

  // przy odejmowaniu odwracamy znak składników prawej
  if (sign === "-") {
    rightTerms = rightTerms.map((t) => {
      const { v, c } = splitCoeffVar(t);
      return formatCoeffVar(-c, v);
    });
  }

  const expected = buildCanonicalExpression([...leftTerms, ...rightTerms]);
  const expression = `${left.data.expression} ${sign} ${right.data.expression}`;

  return {
    type: "AddOrSubtractBinomialTask",
    data: {
      expression,
      leftExpr: left.data.expression,     // do rozbicia na mobile
      rightExpr: right.data.expression,   // do rozbicia na mobile
      leftExpanded: left.data.expected,
      rightExpanded: right.data.expected,
      expected,
      sign,
    },
  };
}

/* ===================== RENDER TASK (polecenie) ===================== */
export function renderTask({ data }) {
  return (
    <div className="cmd-container">
      <style>{`
        .cmd-container { margin-bottom: 14px; }
        .cmd-wide { display: inline; }
        .cmd-narrow { display: none; }
        @media (max-width: 880px) {
          .cmd-container { margin-bottom: 18px; }
          .cmd-wide { display: none; }
          .cmd-narrow { display: block; }
        }
      `}</style>

      {/* Desktop: w jednej linii */}
      <p className="cmd-wide">
        Uprość wyrażenie: <InlineMath math={data.expression} />
      </p>

      {/* Mobile: formuła w osobnej, wyśrodkowanej linii */}
      <div className="cmd-narrow">
        <p>Uprość wyrażenie:</p>
        <BlockMath math={data.expression} />
      </div>
    </div>
  );
}

/* ===================== INPUT ===================== */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder="np. 3xy + 2x - 5"
    />
  );
}

/* ===================== CHECK ANSWER ===================== */
export function checkAnswer({ data }, userAnswer) {
  const left = normalizeExpression(userAnswer);
  const right = normalizeExpression(data.expected);
  return { isCorrect: left === right, expected: data.expected };
}

/* ===================== ANSWER ===================== */
export function renderAnswer({ data }) {
  return (
    <>
      <style>{`
        .ans-wide { display: inline; }
        .ans-narrow { display: none; }
        @media (max-width: 880px) {
          .ans-wide { display: none; }
          .ans-narrow { display: block; }
        }
      `}</style>

      {/* Desktop: inline */}
      <span className="ans-wide">
        <InlineMath math={data.expected} />
      </span>

      {/* Mobile: wyśrodkowany blok */}
      <span className="ans-narrow">
        <BlockMath math={data.expected} />
      </span>
    </>
  );
}

/* ===================== EXPLANATION ===================== */
export function renderExplanation({ data }) {
  const {
    expression, leftExpr, rightExpr,
    leftExpanded, rightExpanded, expected, sign
  } = data;

  // Odwraca znaki wewnątrz sumy, np. "20xz - 10x + 8z - 4" -> "- 20xz + 10x - 8z + 4"
  const flipSigns = (sum) => {
    const s = String(sum).replace(/−/g, "-").trim();
    const withLead = /^[+\-]/.test(s) ? s : `+ ${s}`;
    const parts = withLead.match(/([+\-]\s*[^+\-]+)/g) || [withLead];
    const flipped = parts
      .map(
        (seg) =>
          (seg.trim().startsWith("-") ? "+ " : "- ") +
          seg.trim().replace(/^[+\-]\s*/, "")
      )
      .join(" ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^\+\s*/, "");
    return flipped;
  };

  /* === DESKTOP: łamanie TYLKO po "="; w 2. linijce domagane "=", a przy "−" 3. linia z rozłożeniem minusa === */
  const step1Wide =
    sign === "+"
      ? String.raw`
\begin{array}{c}
${expression} \; = \\[10pt]
=\; ${leftExpanded} \; + \; ${rightExpanded}
\end{array}
`
      : (() => {
          const rf = flipSigns(rightExpanded).replace(/^\+\s*/, "");
          const joiner = rf.startsWith("-") ? String.raw`\;` : String.raw`\; + \;`;
          return String.raw`
\begin{array}{c}
${expression} \; = \\[10pt]
=\; ${leftExpanded} \; - \; (${rightExpanded}) \; = \\[10pt]
=\; ${leftExpanded} ${joiner} ${rf}
\end{array}
`;
        })();

  /* === MOBILE: 4 linie z „łącznikami” + (dla „−”) DODATKOWA 5. linia po rozłożeniu minusa === */
  const rfMobile = flipSigns(rightExpanded).replace(/^\+\s*/, "");
  const rfMobileSign = rfMobile.startsWith("-") ? "-" : "+";
  const rfMobileBody = rfMobile.replace(/^[-+]\s*/, "");

  const step1Narrow =
    sign === "+"
      ? String.raw`
\begin{array}{c}
${leftExpr} \; + \\[10pt]
+\; ${rightExpr} \; = \\[10pt]
=\; ${leftExpanded} \; + \\[10pt]
+\; ${rightExpanded} \; =
\end{array}
`
      : String.raw`
\begin{array}{c}
${leftExpr} \; + \\[10pt]
-\; (${rightExpr}) \; = \\[10pt]
=\; ${leftExpanded} \; + \\[10pt]
-\; (${rightExpanded}) \; = \\[10pt]
=\; ${leftExpanded} \; + \\[10pt]
${rfMobileSign}\; ${rfMobileBody}
\end{array}
`;

  return (
    <div className="exp-wrap" style={{ lineHeight: 1.9 }}>
      <style>{`
        .exp-wrap p { text-align: left; }
        .exp-wrap .katex-display {
          text-align: center !important;
          margin: 12px 0 14px !important;
        }
        .exp-wide { display: block; }
        .exp-narrow { display: none; }
        @media (max-width: 880px) {
          .exp-wide { display: none; }
          .exp-narrow { display: block; }
        }
      `}</style>

      <p><strong>Krok 1:</strong> Rozwiń oba wyrażenia:</p>

      {/* Desktop — po "=" nowy wiersz; 2. linia kończy się "=", 3. linia po rozłożeniu minusa */}
      <div className="exp-wide">
        <BlockMath math={step1Wide} />
      </div>

      {/* Mobile — 4 linie z łącznikami + 5. linia (rozłożenie minusa) */}
      <div className="exp-narrow">
        <BlockMath math={step1Narrow} />
      </div>

      <p><strong>Krok 2:</strong> Zredukuj wyrazy podobne (porządek kanoniczny):</p>
      <BlockMath math={expected} />
    </div>
  );
}

/* ===================== USER ANSWER ===================== */
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <p><i>(brak odpowiedzi)</i></p>;
  return (
    <p>
      <InlineMath math={raw} />
    </p>
  );
}
